import { Item, Collection } from "./collection_base"
import DataLoader from "#lib/data_loader"
import JsonFetch from "#lib/json_fetch"

// The CP2020 skills area — a COLLECTION whose items are skill rows. It is NOT an
// equipment area: it has no cost, it serializes a keyed BLOB (not an array), and it
// GROUPS its items by stat (placing them itself — see insertItem). It also carries the
// chargen orchestration equipment areas don't — package highlighting/reconcile,
// upbringing bonuses, skill-point budgets, seeding — which are cross-cutting over
// the whole set + external data (roles, childhood, stats), so they live here as
// collection methods, reading the items via getAll().
//
// A SKILL ITEM answers for itself: its blob entry(), its bonus total, its ranks.
// effectiveValue (which folds in an external stat) is a collection query, since the
// stat lives outside the item and untrained skills have no item at all.

// One skill row: a <fieldset> with ranks + lifepath_bonus as named controls; name,
// stat, and the derived upbringing_bonus/total are display/data (not submitted).
export class SkillItem extends Item {
  // isSpecialAbility: a role-derived Special Ability (name from the role, level in the
  // ranks slot). Its stat is "" so effectiveValue = level (currentStat("") is 0), and
  // it groups under "SPECIAL" (see groupKeyFor). Package-by-definition (see highlightPackage).
  static values = { slug: String, stat: String, isSpecialAbility: Boolean }

  isSpecialAbility() { return this.isSpecialAbilityValue }

  // Fill self from the hydration entry (base lifecycle: the collection registered the entry
  // + placed us in our group, then Stimulus connected us here). Stamps this row's STORED
  // display — ranks/lifepath controls + the name (full in data-full, abbreviated for show).
  // The derived bits (upbringing/total/package) are NOT filled here: upbringing is stamped
  // by the collection's restampUpbringing over connected items, package by highlightPackage;
  // both run in converge, which re-runs after this fill's changed(). stat + isSpecialAbility
  // ride as Stimulus data-values (stamped pre-connect by addSkill), so they're already set.
  fill(entry) {
    const nameEl = this.el("name")
    if (nameEl) {
      const full = entry.name ?? nameEl.dataset.full ?? ""
      nameEl.dataset.full = full // the source of truth (blob/matching); display is abbreviated
      nameEl.textContent = displayName(full)
    }
    this.setControl("ranks", entry.ranks)
    this.setControl("lifepath_bonus", entry.lifepath_bonus)
    // An SA has only a LEVEL (the ranks input) — blank the LP/UP columns and the remove
    // button. Use `invisible` (visibility:hidden), NOT `hidden` (display:none): display:none
    // removes them from the flex flow, so RANK would slide into the UP column. invisible
    // KEEPS their space, so RANK stays under RANK.
    if (this.isSpecialAbility()) {
      for (const sel of ["[name='lifepath_bonus']", "[data-field='upbringing_bonus']", "[data-action='cp2020--skills#remove']"]) {
        this.element.querySelector(sel)?.classList.add("invisible")
      }
    }
    this.updateTotal()
  }

  // Fill a stored control THROUGH the binding system (bind + set) so ranks/lifepath live in
  // the stash — the item is stash-queryable and a user edit flows back via the fromDom
  // listener. (The item's own filler; skills doesn't use the base builder.)
  setControl(name, value) {
    this.bind(name, this.element.querySelector(`[name='${name}']`), "control")
    this.set(name, value ?? 0)
  }

  // The collection's restampUpbringing sets a row's DERIVED upbringing bonus (over connected
  // items — see the collection). Stashed under upbringing_bonus (queryable; entry() reads it)
  // AND painted to the span; then recompute the total.
  setUpbringing(value) {
    this.bind("upbringing_bonus", this.el("upbringing_bonus"), "text")
    this.set("upbringing_bonus", value)
    this.updateTotal()
  }

  // Blob entry from the STASH for the numeric fields (ranks/lifepath/upbringing) + the
  // data-value/display sources for the rest. ranks/lifepath/upbringing keep intOf coercion:
  // skills has an established INT blob contract (budgets sum them as ints, fixtures store
  // ints) — distinct from the equipment areas' free-text-verbatim fields.
  entry() {
    const e = {
      name: this.name(),
      stat: this.statValue || "",
      ranks: this.ranks(),
      lifepath_bonus: this.lifepath(),
      upbringing_bonus: this.upbringing(),
    }
    if (this.isSpecialAbility()) e.isSpecialAbility = true
    return e
  }

  // The FULL name (blob/export/package-matching use it) — from data-full. The display
  // text is the abbreviated form (see connect); data-full is the source of truth.
  name() { const el = this.el("name"); return el?.dataset.full ?? el?.textContent ?? "" }
  ranks() { return intOf(this.get("ranks")) }
  lifepath() { return intOf(this.get("lifepath_bonus")) }
  upbringing() { return intOf(this.get("upbringing_bonus")) }

  // Ranks + lifepath + upbringing (the skill's own bonus, no stat). "Invested" when
  // the player has spent ranks or lifepath (upbringing is derived, not investment).
  bonusTotal() { return this.ranks() + this.lifepath() + this.upbringing() }
  invested() { return this.ranks() > 0 || this.lifepath() > 0 }

  // Package (career) membership is the item's OWN state — the collection resolves who
  // satisfies the role package and tells each skill; the skill owns the flag + paint.
  setAsPackage(on) { this.element.classList.toggle("skill-package", on) }
  isPackage() { return this.element.classList.contains("skill-package") }

  // A number input changed — refresh this row's total display, then let the
  // collection reassemble + re-highlight (via the bubbling item-changed).
  changed() {
    this.updateTotal()
    super.changed()
  }

  updateTotal() {
    const t = this.el("total")
    if (t) t.textContent = `= ${this.bonusTotal()}`
  }

  el(field) { return this.element.querySelector(`[data-field='${field}']`) }
}

// Skills EDIT path: Rails ships only the row/group <template>s; this controller pulls the
// stored skills blob and renders the rows itself, then converges (recompute — the
// derivation graph). SHOW path still SERVER-renders (revisited later) — SkillsList :show
// mode is untouched.
export class SkillsCollection extends Collection {
  static CATALOGS = ["skills"]
  // Readiness = rows PLACED, not filled (see Collection.READINESS). recompute is an
  // idempotent clear-then-derive graph and each row's post-fill changed() re-converges, so a
  // pass over unfilled rows is corrected, not wrong; and membership — the thing that must
  // never race — is answered by the census, which already counts pending rows.
  static READINESS = "placed"
  static targets = ["field", "groups", "rowTemplate", "groupTemplate", "addSelect", "familySelect", "specInput", "specAdd", "packageSum", "pickupSum"]

  // Outlets: the sheet form (childhood/role/stat reads), the stats grid (INT/REF ->
  // pickup cap), and the role editor (SA ranks -> package spend). Same async pattern
  // as before: hydrate on outletConnected, re-pull on the change messages.
  static outlets = ["cp2020--sheet", "cp2020--stat-broadcast", "cp2020--role"]

  static PACKAGE_CAP = 40
  // Cosmetic group LAYOUT — three fixed columns, each an ordered list of group keys.
  // A skill's group is its stat (LANGUAGE split out; see groupKeyFor); this decides
  // which column that group is painted into and its order within the column. Display
  // only — stat/effectiveValue/matching are untouched. (LUCK/MA are omitted: no skill
  // links to them. "SPECIAL" (special abilities) leads column 0.)
  static COLUMN_LAYOUT = [
    ["SPECIAL", "ATTR", "BODY", "COOL", "EMP"],
    ["INT", "LANGUAGE"],
    ["REF", "TECH"],
  ]

  // Outlet arrival (sheet/role/stat) just schedules a converge — it reads live state.
  cp2020SheetOutletConnected() { this.settle() }
  cp2020StatBroadcastOutletConnected() { this.settle() }
  cp2020RoleOutletConnected() { this.settle() }

  connect() {
    // Warm the catalogs recompute reads: skills (cached for effectiveValue), childhood
    // upbringing, roles + sa_skills (role -> Special Ability resolution).
    DataLoader.systemGet("skills").then((c) => { this._catalog = c })
    DataLoader.systemPreload("childhood-environment")
    DataLoader.systemPreload("roles")
    DataLoader.systemPreload("sa_skills")
    if (this.hasGroupsTarget) this.groupsTarget.classList.remove("hidden")
    super.connect() // the universal frame: hydrate (client-render) + settle (first pass)
  }

  // The stored skills blob is a keyed HASH ({ slug => entry }), not an array — flatten
  // to entries (slug folded in) for the single filler.
  async loadEntries() {
    const { data } = await JsonFetch.get(`/characters/${this.characterValue}/skills`)
    if (!data || typeof data !== "object") return []
    return Object.entries(data).map(([slug, e]) => ({ slug, ...e }))
  }

  // Render one STORED skill row from its entry — routes through the single filler
  // (addSkill), carrying the stored ranks/lifepath + the SA flag. Derived fields
  // (upbringing/total/package) are left to converge. No animation on cold-load.
  renderItem(entry) {
    return this.addSkill({
      slug: entry.slug,
      name: entry.name,
      stat: entry.stat || "",
      isSpecialAbility: !!entry.isSpecialAbility,
      ranks: entry.ranks,
      lifepath_bonus: entry.lifepath_bonus,
      animate: false,
    })
  }

  // The per-collection compute body (the frame's recompute hook). Make the sheet
  // consistent: sweep ungrouped rows into their stat groups, seed an empty sheet,
  // reconcile SAs, restamp upbringing from the childhood env, highlight + resum the role
  // package, reserialize. Every step is idempotent and reads live state; mid-pass adds are
  // visible to has()/slugs() immediately (the base census counts pending items, which an
  // add becomes the moment it registers). The base frame (converge) handles readiness +
  // serialization.
  async recompute() {
    await this.seedDefaultsIfEmpty()
    await this.reconcileSpecialAbilities()
    await this.restampUpbringing()
    await this.highlightPackage()
    this.reassemble()
  }

  // has()/slugs() come from the base — the connection-independent census (connected
  // controllers ∪ pending entries). Skills was where the connect-race first bit (re-seeding
  // over rows that were rendered but not yet connected); the base now owns the answer.

  // --- serialize (blob, not array) ---------------------------------------------
  // Collapse-when-uninvested: {} if nobody has ranks/lifepath, so an untouched sheet
  // doesn't persist ~51 zero rows. Announces skills-changed (consumers re-pull).
  reassemble() {
    const blob = {}
    let invested = false
    for (const item of this.getAll()) {
      if (item.invested()) invested = true
      blob[item.slugValue] = item.entry()
    }
    if (this.hasFieldTarget) this.fieldTarget.value = JSON.stringify(invested ? blob : {})
    this.dispatch("skills-changed", { prefix: "cp2020", target: window })
  }

  // --- add ----------------------------------------------------------------------
  // A catalog skill was picked in the search select.
  async addFromSelect(event) {
    const slug = event.target.value
    if (!slug) return
    event.target.value = "" // sync-clear before the await, so slim-select's duplicate
    // same-slug change echo reads "" and is ignored (see Collection#add). addSkill's
    // has() guard already blocks a true dupe, but this avoids the wasted re-entrant call.
    const s = (await DataLoader.systemGet("skills"))[slug]
    if (s) this.addSkill({ slug, name: catalogLabel(s), stat: s.stat || "" })
    this.resetSelect(event.target)
  }

  // Materialize a skill item from the template and PLACE it in its stat group — SYNCHRONOUS
  // and DATA-FREE beyond the grouping attrs. Stamp slug/stat/SA data-values (these must
  // exist PRE-CONNECT: groupKeyFor reads them right now), register the hydration entry
  // (name/ranks/lifepath — the item fills these on connect), place, dedup the add-select.
  // Returns the new ELEMENT (the derivation still
  // marks it — data-auto-added — pre-connect; that's provenance, not fill). No-op -> null if
  // already present. Reassemble happens once, in converge (the added item's post-fill
  // changed() -> settle -> converge). The SINGLE skill-row creator — derived adds (seed/
  // package/upbringing/SA) and the hydrate of stored rows both go through it (see renderItem).
  addSkill({ slug, name, stat, isSpecialAbility = false, ranks = 0, lifepath_bonus = 0, animate = true }) {
    if (this.has(slug)) return null
    const el = this.rowTemplateTarget.content.cloneNode(true).querySelector(`[data-controller~='${this.itemIdentity}']`)
    el.setAttribute(`data-${this.itemIdentity}-stat-value`, stat)
    if (isSpecialAbility) el.setAttribute(`data-${this.itemIdentity}-is-special-ability-value`, "true")
    // Register the entry (slug + stored display) so the item hydrates itself on connect;
    // this also stamps slug-value + the hydration handle. Name/ranks/lifepath ride in the
    // entry, NOT the DOM — the item's fill() paints them. Derived upbringing/total/package
    // are left to converge. This is ALSO what makes the row count as present: it's pending
    // from here on, so has() sees it immediately — before placement, let alone connect.
    this.registerHydration(el, { slug, name, stat, isSpecialAbility, ranks, lifepath_bonus })
    // Pass the name for alpha placement: the item paints [data-field='name'] on its async
    // connect (fill), so it's EMPTY at insert time — insertItem must not read the span here.
    this.insertItem(el, name)
    this.setSelectOptionDisabled(slug, true)
    if (animate) animateRowIn(el)
    return el
  }

  // --- family-skill add (Knowledge / Martial Art / Expert / Language) -----------
  familyChanged() {
    const famSlug = this.familySelectTarget.value
    const show = !!famSlug
    this.specInputTarget.classList.toggle("hidden", !show)
    this.specAddTarget.classList.toggle("hidden", !show)
    if (!show) return
    this.specInputTarget.setAttribute("list", `skill-family-${famSlug}`)
    this.specInputTarget.value = ""
    this.specInputTarget.focus()
  }

  async addFamilySkill(event) {
    event?.preventDefault?.()
    const famSlug = this.familySelectTarget.value
    const spec = this.specInputTarget.value.trim()
    if (!famSlug || !spec) return
    const fam = (await DataLoader.systemGet("skill-families"))[famSlug]
    if (!fam) return
    this.addSkill({ slug: `${famSlug}_${slugify(spec)}`, name: `${fam.name} (${spec})`, stat: fam.stat || "" })
    this.specInputTarget.value = ""
    this.familySelectTarget.value = ""
    this.specInputTarget.classList.add("hidden")
    this.specAddTarget.classList.add("hidden")
  }

  // --- remove -------------------------------------------------------------------
  remove(event) {
    const item = this.itemFor(event.target)
    if (!item) return
    if (item.invested() || item.upbringing() > 0) {
      if (!window.confirm(`Remove ${item.name() || "this skill"}? It has points or bonuses that will be lost.`)) return
    }
    this.dropItem(item)
    this.settle() // removal doesn't dispatch item-changed -> converge (regroup/serialize)
  }

  // --- childhood-environment upbringing bonuses ---------------------------------
  // Called by converge. upbringing_bonus is FULLY DERIVED from the current childhood env:
  // clear it on every row, then apply the env's grants (adding a row when a grant targets a
  // skill not on the sheet). Applies to CONNECTED items via item.setUpbringing (the item
  // owns its span + total); it no longer reaches into raw pre-connect elements. A grant that
  // ADDS a row can't stamp it this pass (its controller isn't connected yet) — but the add's
  // post-fill changed() drives another converge, and this clear-then-grant loop is idempotent,
  // so the new row is stamped on that next pass. Existence (adding the row) stays here.
  async restampUpbringing() {
    for (const item of this.getAll()) item.setUpbringing(0)
    const slug = this.environmentSlug()
    if (!slug) return
    const [environments, catalog] = await Promise.all([
      DataLoader.systemGet("childhood-environment"),
      DataLoader.systemGet("skills"),
    ])
    const grants = environments[slug]?.skills ?? {}
    for (const [skillSlug, bonus] of Object.entries(grants)) {
      let item = this.get(skillSlug)
      if (!item) {
        const s = catalog[skillSlug]
        if (!s) continue
        // Add the missing granted row (existence). Not connected yet -> stamped next converge.
        this.addSkill({ slug: skillSlug, name: catalogLabel(s), stat: s.stat || "", animate: false })
        continue
      }
      item.setUpbringing(bonus)
    }
  }

  environmentSlug() {
    return this.hasCp2020SheetOutlet ? this.cp2020SheetOutlet.field("childhood_environment") : ""
  }

  // --- special abilities (role-derived skill rows) ------------------------------
  // The chosen role(s) each grant a Special Ability; materialize one SA row per
  // granted SA (name from sa_skills, level in the ranks slot). RECONCILE (not clear-
  // restamp): add missing SAs at level 0, remove SAs whose role is gone, but PRESERVE
  // existing levels (the level is player-set, not derived). SAs are package-by-def
  // (see highlightPackage) so their level folds into the package budget for free.
  async reconcileSpecialAbilities() {
    const [roles, saSkills] = await Promise.all([
      DataLoader.systemGet("roles"),
      DataLoader.systemGet("sa_skills"),
    ])
    const wanted = new Map() // saSlug -> name, for the current role(s)
    for (const rs of this.currentRoleSlugs()) {
      const saSlug = roles?.[rs]?.specialability?.slug
      if (saSlug && saSkills?.[saSlug]) wanted.set(saSlug, saSkills[saSlug].name)
    }
    for (const [saSlug, name] of wanted) {
      if (!this.has(saSlug)) this.addSkill({ slug: saSlug, name, stat: "", isSpecialAbility: true, animate: false })
    }
    for (const item of [...this.getAll()]) {
      if (item.isSpecialAbility() && !wanted.has(item.slugValue)) this.dropItem(item)
    }
  }

  // The character's Special Abilities as { slug, level } — asked by computed-stats
  // (via the skills outlet) to fold SA bonuses into Initiative/Awareness.
  specialAbilities() {
    return this.getAll().filter((i) => i.isSpecialAbility()).map((i) => ({ slug: i.slugValue, level: i.ranks() }))
  }

  // --- seed defaults on a blank sheet -------------------------------------------
  async seedDefaultsIfEmpty() {
    // Empty = no rows at all, connection-independently (covers hydrate's stored rows and
    // converge's mid-pass adds, which getAll can't see until they connect). Without this a
    // first converge racing ahead of hydrate's row connects would re-seed over the stored
    // sheet.
    if (this.slugs().size) return
    const catalog = await DataLoader.systemGet("skills")
    for (const [slug, s] of Object.entries(catalog)) {
      if (s.seedOnCreate) this.addSkill({ slug, name: catalogLabel(s), stat: s.stat || "", animate: false })
    }
  }

  // --- package highlighting + reconcile -----------------------------------------
  async highlightPackage() {
    const slugs = this.currentRoleSlugs()
    const [roles, catalog] = await Promise.all([DataLoader.systemGet("roles"), DataLoader.systemGet("skills")])
    const tokens = slugs.flatMap((s) => roles?.[s]?.skills ?? [])
    this.reconcilePackageSkills(tokens, catalog)

    // Resolve who satisfies the package, then tell each skill its own membership.
    const satisfiers = new Set()
    for (const token of tokens) {
      const item = this.bestSatisfier(token)
      if (item) satisfiers.add(item)
    }
    // SAs are package by definition; other skills are package iff they satisfy a token.
    for (const item of this.getAll()) item.setAsPackage(item.isSpecialAbility() || satisfiers.has(item))
    this.updateBudgets()
  }

  reconcilePackageSkills(tokens, catalog) {
    const wantSlugs = new Set()
    for (const token of tokens) {
      if (Array.isArray(token) || familyWildcard(token)) continue
      const slug = slugForName(token, catalog)
      if (slug) wantSlugs.add(slug)
    }
    for (const slug of wantSlugs) {
      if (this.has(slug)) continue
      const s = catalog[slug]
      if (!s) continue
      // Mark auto-added on the returned element (controller not connected yet).
      this.addSkill({ slug, name: catalogLabel(s), stat: s.stat || "", animate: !!tokens.length })
        ?.setAttribute("data-auto-added", "")
    }
    for (const item of [...this.getAll()]) {
      const slug = item.slugValue
      if (!slug || wantSlugs.has(slug)) continue
      if (!item.element.hasAttribute("data-auto-added")) continue
      if (catalog[slug]?.seedOnCreate) continue
      if (item.ranks() > 0 || item.lifepath() > 0) continue
      if (item.upbringing() > 0) continue
      this.dropItem(item)
    }
  }

  bestSatisfier(token) {
    const candidates = Array.isArray(token) ? token.flatMap((t) => this.matchingItems(t)) : this.matchingItems(token)
    if (!candidates.length) return null
    return candidates.reduce((best, i) => (i.ranks() > best.ranks() ? i : best))
  }

  matchingItems(token) {
    const fam = familyWildcard(token)
    if (fam) return this.getAll().filter((i) => i.slugValue?.startsWith(`${fam}_`))
    const key = normalize(token)
    return this.getAll().filter((i) => i.slugValue && normalize(i.name()) === key)
  }

  // --- skill-point budgets ------------------------------------------------------
  updateBudgets() {
    const items = this.getAll()
    const sumRanks = (which) => items.filter(which).reduce((n, i) => n + i.ranks(), 0)
    // Package = every package skill's ranks; pickup = every non-package skill's ranks.
    // SAs are package items whose level lives in the ranks slot, so their level folds
    // into the package sum here for FREE — no more reaching to the role editor.
    const pkg = sumRanks((i) => i.isPackage())
    const pickup = sumRanks((i) => !i.isPackage())
    const cap = this.hasCp2020StatBroadcastOutlet
      ? this.cp2020StatBroadcastOutlet.value("INT") + this.cp2020StatBroadcastOutlet.value("REF")
      : 0
    this.setBudget("packageSum", "Package", pkg, this.constructor.PACKAGE_CAP, this.currentRoleSlugs().length > 0)
    this.setBudget("pickupSum", "Pickup", pickup, cap, true)
  }

  setBudget(name, label, n, cap, show) {
    const has = `has${name[0].toUpperCase()}${name.slice(1)}Target`
    if (!this[has]) return
    const target = this[`${name}Target`]
    if (!show) { target.textContent = ""; return }
    target.textContent = `${label}: ${n}/${cap}`
    const over = n > cap
    target.classList.toggle("text-red-500", over)
    target.classList.toggle("text-gray-500", !over)
  }

  // --- query interface (consumers ask via outlet) -------------------------------
  // Effective value of a skill = current linked stat + the skill's bonus total.
  // Present -> its item; absent -> untrained (catalog stat − 2). SA bonuses are
  // folded in by the specific consumer, not here.
  effectiveValue(slug) {
    const item = this.getAll().find((i) => i.slugValue === slug)
    if (item) return this.currentStat(item.statValue) + item.bonusTotal()
    return this.currentStat(this._catalog?.[slug]?.stat || "") - 2
  }

  currentStat(statAbbr) {
    if (!statAbbr) return 0
    const k = statAbbr.toLowerCase()
    return this.formInt(`stat_${k}`) + this.formInt(`stat_${k}_bonus`)
  }
  formInt(name) { return this.hasCp2020SheetOutlet ? this.cp2020SheetOutlet.intField(name) : 0 }

  currentRoleSlugs() {
    if (!this.hasCp2020SheetOutlet) return []
    return ["role_0", "role_1"].map((r) => this.cp2020SheetOutlet.field(r)).filter(Boolean)
  }

  // --- grouping -----------------------------------------------------------------
  // Skills' items are placed into COSMETIC stat-group <div>s (not appended flat like
  // equipment). The group divs are transparent to fieldset-nesting, so getAll() /
  // toTree still sees the flat list of skill-item fieldsets — grouping is display only.
  //
  // This is skills' own placement method, NOT a base override — the base's placement seam
  // is homeFor(entry), which returns a container for renderItem to append into. Skills
  // can't use it: it needs to place at a computed POSITION within the group (alphabetical),
  // not just pick a parent. addSkill calls this directly instead.
  // name is passed by the caller (addSkill) for alpha placement. The row's [data-field=
  // 'name'] span is painted later, on the item's async connect, so it's empty at insert —
  // for BOTH this row and its already-placed siblings during a synchronous cold-load. So we
  // stamp the name into a data attribute (available immediately), and insertAlpha compares
  // on that, not the unpainted span. Falls back to the span only if a caller omits the name.
  insertItem(el, name = el.querySelector("[data-field='name']")?.textContent ?? "") {
    el.dataset.sortName = name
    insertAlpha(this.groupFor(this.groupKeyFor(el)), el, name)
  }

  // The COSMETIC group a skill lands in — its stat, EXCEPT languages get their own
  // LANGUAGE group. Display-only: the skill's stat / effectiveValue / matching are
  // untouched; only which box it's painted into changes. (This is the seam the old
  // controller reserved for exactly this — routing a family to its own group.)
  groupKeyFor(el) {
    if (el.getAttribute(`data-${this.itemIdentity}-is-special-ability-value`) === "true") return "SPECIAL"
    const slug = el.getAttribute(`data-${this.itemIdentity}-slug-value`) || ""
    if (slug.startsWith("language")) return "LANGUAGE"
    return el.getAttribute(`data-${this.itemIdentity}-stat-value`)
  }

  dropItem(item) {
    const group = item.element.closest("[data-cp2020--skills-group]")
    const slug = item.slugValue
    this.removeItemElement(item.element) // also clears any pending registration

    if (group && !group.querySelector(`[data-controller~='${this.itemIdentity}']`)) group.remove()
    this.setSelectOptionDisabled(slug, false)
  }

  groupFor(groupKey) {
    const existing = this.groupsTarget.querySelector(`[data-cp2020--skills-group='${groupKey}']`)
    if (existing) return existing
    const group = this.groupTemplateTarget.content.cloneNode(true).querySelector("[data-cp2020--skills-group]")
    group.setAttribute("data-cp2020--skills-group", groupKey)
    const heading = group.querySelector("[data-cp2020--skills-target='groupHeading']")
    if (heading) heading.textContent = groupKey
    this.insertGroupInColumn(group, groupKey)
    return group
  }

  // Which column a group lives in, and its order within it, per COLUMN_LAYOUT.
  columnIndexOf(key) {
    const i = this.constructor.COLUMN_LAYOUT.findIndex((col) => col.includes(key))
    return i === -1 ? this.constructor.COLUMN_LAYOUT.length - 1 : i // unknown -> last column
  }
  orderInColumn(key) {
    const col = this.constructor.COLUMN_LAYOUT[this.columnIndexOf(key)]
    const i = col.indexOf(key)
    return i === -1 ? col.length : i
  }

  // Insert the group into its assigned column div, before the first sibling whose
  // within-column order is later.
  insertGroupInColumn(group, groupKey) {
    const col = this.groupsTarget.querySelector(`[data-cp2020--skills-column='${this.columnIndexOf(groupKey)}']`)
    const idx = this.orderInColumn(groupKey)
    const after = [...col.querySelectorAll("[data-cp2020--skills-group]")].find(
      (g) => this.orderInColumn(g.getAttribute("data-cp2020--skills-group")) > idx,
    )
    col.insertBefore(group, after ?? null)
  }

  setSelectOptionDisabled(slug, disabled) {
    if (!this.hasAddSelectTarget) return
    const opt = this.addSelectTarget.querySelector(`option[value='${CSS.escape(slug)}']`)
    if (!opt) return
    opt.disabled = disabled
    this.addSelectTarget.slimSelectRefresh?.()
  }

  // The skill item controller that owns the clicked element.
  itemFor(node) {
    const el = node.closest(`[data-controller~='${this.itemIdentity}']`)
    return el ? this.application.getControllerForElementAndIdentifier(el, this.itemIdentity) : null
  }
}

// --- helpers -------------------------------------------------------------------
const intOf = (v) => { const n = parseInt(v, 10); return Number.isNaN(n) ? 0 : n }
const catalogLabel = (s) => (s.option ? `${s.name} (${s.option})` : s.name)

// Family names abbreviated for DISPLAY only (mirrors SkillsList::FAMILY_ABBREV). The
// full name is kept in data-full, so blob/matching are unaffected.
const FAMILY_ABBREV = { "Martial Art": "MA" }
const displayName = (full) => {
  const m = String(full).match(/^(.+?) \((.+)\)$/)
  return m && FAMILY_ABBREV[m[1]] ? `${FAMILY_ABBREV[m[1]]}: ${m[2]}` : full
}
const normalize = (str) => String(str).toLowerCase().replace(/[^a-z0-9]/g, "")
const slugify = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")

function slugForName(name, catalog) {
  const key = normalize(name)
  for (const [slug, s] of Object.entries(catalog)) {
    const full = s.option ? `${s.name}: ${s.option}` : s.name
    if (normalize(full) === key || normalize(s.name) === key) return slug
  }
  return null
}

function familyWildcard(token) {
  const m = String(token).match(/^(.*?):\s*(any|choose one)\s*$/i)
  if (!m) return null
  const base = m[1].trim().toLowerCase()
  if (base === "area knowledge") return "knowledge"
  return base.replace(/[^a-z0-9]+/g, "_")
}

// Insert an element into a group in alphabetical (by name) order. Compares on each row's
// data-sort-name (stamped at insert) — NOT the [data-field='name'] span, which is painted
// later on the item's async connect and is empty during a synchronous cold-load.
function insertAlpha(group, el, name) {
  const key = name.toLowerCase()
  const after = [...group.querySelectorAll("[data-cp2020--skills-target='row']")].find(
    (r) => (r.dataset.sortName ?? "").toLowerCase() > key,
  )
  group.insertBefore(el, after ?? null)
}

function animateRowIn(row) {
  row.classList.add("skill-row-enter")
  void row.offsetWidth
  requestAnimationFrame(() => row.classList.remove("skill-row-enter"))
}
